import {
  fetchPosts,
  fetchCategoryPosts,
  createTopic,
  resetError,
  resetPosts,
} from "../../redux/features/postsSlice";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { showCustomToast } from "../../utils/toastUtil";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "../partials/Search";
import "react-quill/dist/quill.snow.css";
import PostItem from "./PostItem";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAllNotifications,
  fetchCategories,
  fetchTags,
  searchTags,
} from "../../helpers/querycalls";
import _, { debounce } from "lodash";
import { getQuillModules, imageHandler } from "../../utils/quillUtil";
import { CustomReactQuill } from "../Editor/CustomReactQuill";
import { ShimmerBadge } from "react-shimmer-effects";
import PostShimmer from "../partials/postShimmer";
import { Helmet } from "react-helmet";
import { AnimatePresence, motion } from "framer-motion";

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tagsDropdownRef = useRef(null);

  const userObj = localStorage.getItem("salla_discourse_user");
  const user = JSON.parse(userObj);

  // if the user is logged in and haven't visited the start page yet redirect him to it
  if (user && !localStorage.getItem(user?.username)) {
    navigate("/starter");
  }

  // =======================
  // Redux State Management
  // =======================
  const {
    posts,
    status: postsStatus,
    error: postsError,
    loading: postsLoading,
    arePostsLeft,
  } = useSelector((state) => state.posts);
  const [postTags, setPostTags] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [filteringTags, setFilteringTags] = useState(false);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);

  const { data: tags } = useQuery({
    queryKey: ["tags", selectedCategory?.id],
    queryFn: () => fetchTags(selectedCategory?.id),
    retry: false,
    staleTime: 15 * 1000, //15 seconds
    refetchOnWindowFocus: false,
    enabled: formVisible && !!selectedCategory,
  });

  useEffect(() => {
    if (tags) {
      setPostTags(tags);
    }
  }, [tags]);

  const arePostsLeftRef = useRef(arePostsLeft); // Create a ref to store the latest value

  // Update the ref whenever `arePostsLeft` changes
  useEffect(() => {
    arePostsLeftRef.current = arePostsLeft;
  }, [arePostsLeft]);

  const debouncedHandleTagSearch = debounce((value) => {
    handleTagSearch(value);
  }, 300);

  const handleTagSearch = async (query) => {
    setFilteringTags(true);
    const result = await searchTags(query);
    setFilteringTags(false);
    if (result) {
      setPostTags(result);
    }
  };

  const handleScrollBottomTouch = () => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    // if(postsStatus === "loading"){
    //     console.log("nothing or start");
    //     return;
    // }
    if (scrollHeight - scrollTop - clientHeight <= 5) {
      if (arePostsLeftRef.current) {
        if (filterCategory) {
          dispatch(fetchCategoryPosts(filterCategory?.id, sortSelected));
        } else {
          dispatch(fetchPosts(sortSelected));
        }
      }
    }
  };

  const debouncedHandleScrollBottomTouch = _.debounce(
    handleScrollBottomTouch,
    300
  );
  const [sortSelected, setSortSelected] = useState("new");
  const [filterCategory] = useState("");

  useEffect(() => {
    window.addEventListener("scroll", debouncedHandleScrollBottomTouch);

    return () => {
      window.removeEventListener("scroll", debouncedHandleScrollBottomTouch);
    };
  }, [sortSelected, filterCategory, debouncedHandleScrollBottomTouch]);
  // =======================
  // User Authentication
  // =======================

  let isLoggedin = false;
  if (user) {
    isLoggedin = true;
  }

  // =======================
  // useRefs
  // =======================
  const modalRef = useRef(null);
  const quillRef = useRef();
  const dropdownRef = useRef(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isModalOpen = searchParams.get("create-post") === "true";
  const handleCreatePostClick = () => {
    if (!isLoggedin) {
      window.location.href =
        process.env.REACT_APP_API_URL + `/auth/oauth2_basic`;
    } else {
      // Preserve existing search params
      const params = new URLSearchParams(location.search);
      params.set("create-post", "true");
      navigate({ search: params.toString() }, { replace: true });
    }
  };
  useEffect(() => {
    if (isModalOpen && !formVisible) {
      setFormVisible(true);
    }
    if (!isModalOpen && formVisible) {
      setFormVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);
  useEffect(() => {
    if (isModalOpen && !isLoggedin) {
      const params = new URLSearchParams(location.search);
      params.delete("create-post");
      navigate({ search: params.toString() }, { replace: true });
      window.location.href =
        process.env.REACT_APP_API_URL + `/auth/oauth2_basic`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, isLoggedin]);
  const handleClose = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("create-post");
    navigate({ search: params.toString() }, { replace: true });

    // Existing state cleanup
    setNewPost({ title: "", category: "", raw: "" });
    setSelectedTag([]);
    setSelectedCategory(null);
    dispatch(resetError());
    setIsDropdownOpen(false);
  }, [navigate, location.search, dispatch]);

  // =======================
  // framer motion
  // =======================

  const options = {
    initial: { y: "100vh" },
    animate: { y: 0, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { y: "100vh", transition: { duration: 0.3, ease: "easeIn" } },
  };

  // =======================
  // Computed States
  // =======================

  const [combinedCategories, setCombinedCategories] = useState(null);
  useEffect(() => {
    setCombinedCategories(
      categories?.reduce((acc, category) => {
        acc.push(category);
        if (
          category.subcategory_count > 0 &&
          Array.isArray(category.subcategory_list)
        ) {
          acc.push(...category.subcategory_list);
        }

        return acc;
      }, [])
    );
  }, [categories]);

  const [filteredUserCategories, setFilteredUserCategories] = useState(null);
  useEffect(() => {
    setFilteredUserCategories(
      user?.admin
        ? combinedCategories
        : combinedCategories?.filter(
            (item) => item.only_admin_can_post === false
          )
    );
  }, [combinedCategories, user?.admin]);

  // useEffect(() => {
  //     setSelectedCategory(filteredUserCategories?.[0]?.id);
  // }, [filteredUserCategories]);

  const filteredPosts = posts.filter(
    (post) => post
    // post.cooked.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // =======================
  // useStates
  // =======================
  // const [filterSelected, setFilterSelected] = useState(null);
  const [newPost, setNewPost] = useState({
    title: "",
    category: "",
    raw: "",
  });

  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState([]);
  const [modalMaximized, setModalMaximized] = useState(false);
  const [isTabsDropDownOpen, setIsTabsDropDownOpen] = useState(false);

  // =======================
  // useEffects
  // =======================

  useEffect(() => {
    if (
      filteredUserCategories?.length > 0 &&
      !selectedCategory?.hasOwnProperty("name")
    ) {
      setSelectedCategory({
        value: filteredUserCategories[0]?.name,
        name: filteredUserCategories[0]?.name,
        id: filteredUserCategories[0]?.id,
        image: filteredUserCategories[0]?.uploaded_logo?.url,
      });
    }
  }, [filteredUserCategories, selectedCategory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !modalMaximized &&
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !event.target.closest(".custom-toast")
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClose, modalMaximized]);

  useEffect(() => {
    if (formVisible) {
      // Disable scrolling on body when modal is open
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = "auto";
    }

    return () => {
      // Cleanup to ensure no leftover styles
      document.body.style.overflow = "auto";
    };
  }, [formVisible]);

  useEffect(() => {
    if (Array.isArray(postsError)) {
      postsError.forEach((error) => {
        showCustomToast(error, "error");
      });
      dispatch(resetError());
    }
  }, [postsError, dispatch]);

  useEffect(() => {
    dispatch(resetPosts());
    if (filterCategory) {
      dispatch(fetchCategoryPosts(filterCategory?.id, sortSelected));
    } else {
      dispatch(fetchPosts(sortSelected));
    }
  }, [filterCategory, sortSelected, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsTabsDropDownOpen(false); // Close the dropdown if you click outside it
      }
    };

    if (isTabsDropDownOpen) {
      document.addEventListener("mousedown", handleClickOutside); // Add listener to window when dropdown is open
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Clean up listener if it's closed
    };
  }, [isTabsDropDownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tagsDropdownRef.current &&
        !tagsDropdownRef.current.contains(event.target)
      ) {
        toggleTagDropdown(false); // Close the dropdown if you click outside it
      }
    };

    if (isTagDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside); // Add listener to window when dropdown is open
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Clean up listener if it's closed
    };
  }, [isTagDropdownOpen]);

  const modules = useMemo(
    () => getQuillModules(quillRef, imageHandler),
    [quillRef]
  );

  // =======================
  // handlers
  // =======================

  function handleChangeSortSelected(sort) {
    dispatch(resetPosts());
    setSortSelected(sort);
    if (filterCategory) {
      dispatch(fetchCategoryPosts(filterCategory?.id, sort));
    } else {
      dispatch(fetchPosts(sort));
    }
  }

  async function handleFormSubmit(e) {
    e.preventDefault();

    if (!selectedTag.length) {
      showCustomToast("يرجى اضافه الوسم", "error");
      return;
    } else if (selectedCategory.id === 11) {
      showCustomToast("الرجاء اختيار مجتمع", "error");
      return;
    }

    try {
      const editor = quillRef.current.getEditor();
      let htmlValue = editor.root.innerHTML; // Get HTML content

      const result = await dispatch(
        createTopic({
          ...newPost,
          raw: htmlValue,
          tag: selectedTag,
          category: selectedCategory?.id,
        })
      );

      queryClient.invalidateQueries(["tags"]);
      if (result) {
        handleClose();

        navigate("/");
      }
    } catch (err) {
      console.error("Error creating post:", err);
    }
  }

  // =======================
  // togglers
  // =======================

  function toggleDropdown() {
    setIsDropdownOpen((prev) => !prev);
  }

  function toggleTabsDropDown() {
    setIsTabsDropDownOpen((prev) => !prev);
  }

  //prevent scrolling when dropdown is open
  useEffect(() => {
    if (isTabsDropDownOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isTabsDropDownOpen]);

  function toggleTagDropdown() {
    setIsTagDropdownOpen((prev) => !prev);
  }
  useQuery({
    queryKey: ["allNotifications"],
    queryFn: fetchAllNotifications,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30000, // Auto-refetch every 30 seconds
    enabled: isLoggedin, // Only run the query if the user is logged in
  });
  return (
    <>
      <Helmet>
        <title>مجتمع سلة</title>
        <meta name="description" content="مجتمع سلة" />
      </Helmet>
      <div className="flex justify-end w-full">
        <AnimatePresence>
          {formVisible && isLoggedin && (
            <div className="fixed inset-0 z-50 flex justify-center items-end bg-opacity-20">
              <motion.div
                variants={options}
                initial="initial"
                animate="animate"
                exit="exit"
                ref={modalRef}
                style={{ boxShadow: "0px 10px 100px 12px rgba(0, 0, 0, 0.1)" }}
                className={`relative flex flex-col   ${
                  modalMaximized
                    ? "w-full h-full"
                    : "sm:w-[1220px] h-full sm:h-auto w-full"
                } rounded-lg bg-[#fbfdfe] shadow-lg overflow-hidden`}
                dir="rtl"
              >
                <div className="px-4 md:px-10 pt-8 top-8 flex justify-between">
                  <div className="flex gap-5 items-center">
                    <img
                      src="/images/modalpen.png"
                      alt=""
                      className="cursor-pointer h-[48px]"
                    />
                    <span className="text-[20px] sm:text-[24px] font-medium sm:font-bold my-0.5">
                      منشور جديد
                    </span>
                  </div>

                  <div className="flex gap-5 sm:gap-10">
                    <img
                      src="/images/expand.svg"
                      alt=""
                      className="sm:block hidden cursor-pointer w-[24px] h-[24px]"
                      onClick={() => setModalMaximized((prev) => !prev)}
                    />
                    <img
                      src="/images/home/cross.svg"
                      alt=""
                      className="cursor-pointer w-[24px] h-[24px]"
                      onClick={() => {
                        setModalMaximized(false);
                        handleClose();
                      }}
                    />
                  </div>
                </div>
                <hr
                  className="mt-5"
                  style={{
                    height: "1px",
                    borderWidth: 0,
                    color: "#F1F8F9",
                    backgroundColor: "#F1F8F9",
                  }}
                />
                <div
                  className={`overflow-y-auto pt-5 sm:max-h-[85svh] custom-scrollbar scroll-ms-4 md:px-10 px-4 ${
                    modalMaximized ? "h-full" : ""
                  } sm:h-auto h-full`}
                >
                  <form
                    className={`flex md:flex-row flex-col gap-x-11 ${
                      modalMaximized ? "justify-center" : ""
                    }`}
                    onSubmit={handleFormSubmit}
                  >
                    <div className="flex flex-col md:max-w-[240px] mt-2.5">
                      <p className="text-[#444444] text-[15px] font-medium">
                        اكتب موضوع جديد في
                      </p>
                      <div className="hidden md:flex flex-col gap-4 rounded-md pt-2">
                        {filteredUserCategories?.map((category) => {
                          // Check if subcategory_ids has length of 0
                          if (category?.subcategory_ids.length === 0) {
                            return (
                              <div
                                key={category.id}
                                className={`flex cursor-pointer items-center ${
                                  selectedCategory?.name === category.name
                                    ? "bg-[#E4FAF5] border-[1px] border-[#76E8CD] text-[#004D5A]"
                                    : ""
                                } rounded-lg`}
                                onClick={() =>
                                  setSelectedCategory({
                                    value: category?.name,
                                    name: category?.name,
                                    id: category?.id,
                                  })
                                }
                              >
                                {category?.uploaded_logo ? (
                                  <>
                                    <img
                                      loading="eager"
                                      src={
                                        process.env.REACT_APP_API_URL +
                                        category?.uploaded_logo?.url
                                      }
                                      className="inline-block max-w-6 max-h-6 ms-2.5"
                                      alt="emoji"
                                    />{" "}
                                    <span className="py-[8px] px-[10px] text-[16px]">
                                      {category.name}
                                    </span>
                                  </>
                                ) : (
                                  <span className="py-[8px] px-[10px] text-[16px]">
                                    {category.name}
                                  </span>
                                )}
                              </div>
                            );
                          }
                          return null; // Explicitly return null if the condition is not met
                        })}
                      </div>
                      <div className="block md:hidden">
                        <div
                          className="cursor-pointer mt-[8px] border-[1px] border-[#DDDDDD] rounded-md flex gap-5 py-[10px] px-[20px] items-center justify-between"
                          onClick={toggleDropdown}
                        >
                          <span
                            className={
                              selectedCategory?.id === 11
                                ? "text-base text-[#999]"
                                : "text-base text-[#004D5A]"
                            }
                          >
                            {selectedCategory?.id !== 11 ? (
                              selectedCategory?.image ? (
                                <>
                                  <img
                                    loading="eager"
                                    src={
                                      process.env.REACT_APP_API_URL +
                                      selectedCategory?.image
                                    }
                                    className="inline-block max-w-6 max-h-6 me-2.5"
                                    alt="emoji"
                                  />{" "}
                                  <span className="text-[16px]">
                                    {selectedCategory?.name}
                                  </span>
                                </>
                              ) : (
                                <span className="py-[8px] text-[16px]">
                                  {selectedCategory?.name}
                                </span>
                              )
                            ) : (
                              "اختر المجتمع"
                            )}
                          </span>
                          <img
                            src="/images/home/chevron-down.svg"
                            alt=""
                            className={`${isDropdownOpen ? "-rotate-180" : ""}`}
                          />
                        </div>

                        {isDropdownOpen && (
                          <div className="p-1 absolute mt-2 sm:w-[95.5%] w-[92%] border-[1px] border-[#DDDDDD] rounded-md bg-white z-50">
                            <div className=" overflow-auto max-h-[250px] scrollbar-thiner">
                              <ul>
                                {filteredUserCategories.map((category) => {
                                  // Check if subcategory_ids has length of 0
                                  if (category?.subcategory_ids.length === 0) {
                                    return (
                                      <li
                                        key={category?.id}
                                        className="z-200 px-[20px] py-[10px] cursor-pointer hover:bg-gray-200"
                                        onClick={() => {
                                          setIsDropdownOpen(false);
                                          setSelectedCategory({
                                            value: category?.name,
                                            name: category?.name,
                                            id: category?.id,
                                            image: category?.uploaded_logo?.url,
                                          });
                                        }}
                                      >
                                        {category?.uploaded_logo ? (
                                          <>
                                            <img
                                              loading="eager"
                                              src={
                                                process.env.REACT_APP_API_URL +
                                                category?.uploaded_logo?.url
                                              }
                                              className="inline-block max-w-6 max-h-6"
                                              alt="emoji"
                                            />{" "}
                                            <span className="px-[10px] text-[16px]">
                                              {category?.name}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="py-[8px] px-[10px] text-[16px]">
                                            {category.name}
                                          </span>
                                        )}
                                      </li>
                                    );
                                  }
                                  return null; // Explicitly return null if condition is not met
                                })}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="md:mt-12 mt-8">
                        {user?.admin && (
                          <div className="flex justify-between text-medium text-[#444444]">
                            <span>سهّل البحث عن منشورك عبر إضافة وسوم</span>
                          </div>
                        )}
                        <div
                          className="cursor-pointer mt-[8px] border-[1px] border-[#DDDDDD] rounded-[4px] flex gap-5 py-[10px] px-[20px] items-center"
                          onClick={toggleTagDropdown}
                        >
                          <img src="/images/home/tag.svg" alt="Tag" />
                          <input
                            type="text"
                            onChange={(e) => {
                              if (e.target.value.length > 0) {
                                debouncedHandleTagSearch(e.target.value);
                              } else {
                                setPostTags(tags);
                              }
                            }}
                            className="text-[14px] placeholder:text-[#BBBBBB] outline-none border-none w-full bg-transparent"
                            placeholder="أضف وسوم مناسبة"
                          />
                        </div>

                        <div
                          className="flex flex-wrap gap-2 mt-5"
                          style={
                            isTagDropdownOpen
                              ? { display: "none" }
                              : { display: "flex" }
                          }
                        >
                          {selectedTag.map((tag) => (
                            <div className="bg-[#BAF3E6] text-[#004D5A] px-1.5 py-0.5 flex gap-2 rounded-full text-[14px] items-center">
                              <p>{tag.name}</p>
                              <img
                                className="cursor-pointer"
                                onClick={() => {
                                  setSelectedTag((prevTags) =>
                                    prevTags.filter((t) => t.id !== tag.id)
                                  );
                                }}
                                src="/images/remove-tag.svg"
                                alt="X"
                              />
                            </div>
                          ))}
                        </div>

                        {isTagDropdownOpen && postTags.length > 0 && (
                          <div
                            ref={tagsDropdownRef}
                            className="p-1 mt-2 border-[1px] border-[#DDDDDD] bg-white rounded-md shadow-md"
                          >
                            <div className="overflow-auto max-h-[142px] scrollbar-thiner">
                              {filteringTags ? (
                                <div className="flex justify-center items-center h-[142px]">
                                  <img
                                    loading="eager"
                                    src="/images/salla_loader_big.svg"
                                    alt="Loading..."
                                    className="w-[32px] h-[32px] loader self-center"
                                  />
                                </div>
                              ) : (
                                <ul>
                                  {postTags.map((tag) => (
                                    <li
                                      key={tag.id}
                                      className="px-[20px] py-[10px] cursor-pointer hover:bg-gray-200 rounded-sm transition-colors me-1"
                                      onClick={() => {
                                        setSelectedTag((prevTags) => {
                                          if (
                                            !prevTags.some(
                                              (t) => t.id === tag.id
                                            )
                                          ) {
                                            return [
                                              ...prevTags,
                                              { name: tag.value, id: tag.id },
                                            ];
                                          }
                                          return prevTags;
                                        });
                                        setIsTagDropdownOpen(false);
                                      }}
                                    >
                                      {tag.value}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col rounded-lg border-[#EEE] border max-w-[780px] w-full shadow-md max-md:mt-6">
                      <div>
                        <input
                          required={true}
                          type="text"
                          id="title"
                          name="title"
                          value={newPost.title}
                          onChange={(e) =>
                            setNewPost({ ...newPost, title: e.target.value })
                          }
                          className=" h-[54px] p-2 px-5 w-full border-b-0 rounded-lg rounded-b-none placeholder:text-[#BBBBBB]"
                          placeholder="اكتب عنوان لمنشورك"
                        />
                        <CustomReactQuill
                          quillRef={quillRef}
                          modules={modules}
                          placeholder="شارك أفكارك، أسئلتك، أو أيّ موضوع يهم مجتمع سلة."
                        />
                      </div>
                      <div className="flex justify-end gap-4 py-8 px-10">
                        <button
                          type="submit"
                          className="transition-colors btn bg-blue-500 px-6 py-2.5 text-white rounded-[4px]"
                        >
                          أضف المنشور
                        </button>
                      </div>
                    </div>
                  </form>
                  <p className="text-center text-[#999999] text-base font-normal my-20">
                    اطّلع على ضوابط المشاركة؛ للمحافظة على نقاشات مُثرية وملهمة
                    تترك أثرًا إيجابيًا في مجتمع سلة
                  </p>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        <div className="sm:p-6 !pt-0 sm:mt-4 w-full" dir="rtl">
          <div
            style={{ backgroundImage: "url('/images/home/home-bg.png')" }}
            className="flex flex-col items-center justify-center py-[60px] px-[40px] rounded-[10px]"
          >
            <img src="/images/search-logo.svg" alt="logo" />
            <div className="my-6 flex flex-col gap-y-3">
              <h1 className="text-center text-[#004956] text-3xl font-bold">
                مجتمع التجارة الالكترونية
              </h1>
              <h2 className="text-center text-[#004956] text-2xl font-normal">
                من التجار الى التجار
              </h2>
            </div>
            <Search
              type="local"
              placeholder="ابحث عن سؤالك هنا"
              setFormVisible={setFormVisible}
              setNewPost={setNewPost}
            />
          </div>

          <div
            className="flex justify-center relative"
            style={{ zIndex: isTabsDropDownOpen ? 1000 : 0 }}
          >
            {/* right part */}
            <div className="w-full mt-4 relative">
              {!(isLoading || postsStatus === "loading") && (
                <div className="flex justify-center items-center sm:pt-10 pb-3 sm:sticky top-[87px] pt-3 sm:top-[5rem] bg-[#f8f8f8]">
                  <div className="flex justify-between items-center w-full max-w-[1000px]">
                    {" "}
                    {/* Adjust max-width and padding as needed */}
                    <button
                      onClick={toggleTabsDropDown}
                      className="gap-x-2 sm:!hidden flex items-center mx-4 justify-center text-[14px] font-medium text-[#004D5A] border py-3 rounded-md w-full"
                    >
                      <span>
                        ترتيب:{" "}
                        {sortSelected === "new"
                          ? "الأحدث"
                          : sortSelected === "latest"
                          ? "الأكثر مشاهدة"
                          : "تم الاجابة عنه"}
                      </span>
                      <img src="/images/chevron-down.svg" alt="chevron" />
                    </button>
                    <div
                      style={
                        isTabsDropDownOpen
                          ? { display: "flex" }
                          : { display: "none" }
                      }
                      className="sm:!flex hidden sm:flex-row flex-col max-sm:justify-end lg:gap-10 gap-5 items-center lg:text-[18px] text-[15px] max-sm:fixed inset-0 max-sm:bg-[#004D5A] !bg-opacity-50 max-sm:border sm:rounded-lg sm:h-fit h-[100dvh]"
                    >
                      <span
                        className={
                          sortSelected === "new"
                            ? `text-[#333333] border-b-[2px] border-[#999999] pb-1.5 cursor-pointer sm:block hidden`
                            : `text-[#666666] pb-2 cursor-pointer sm:block hidden`
                        }
                        onClick={() => {
                          handleChangeSortSelected("new");
                        }}
                      >
                        الأحدث
                      </span>
                      <span
                        className={
                          sortSelected === "latest"
                            ? `text-[#333333] border-b-[2px] border-[#999999] pb-1.5 cursor-pointer sm:block hidden`
                            : `text-[#666666] pb-2 cursor-pointer sm:block hidden`
                        }
                        onClick={() => {
                          handleChangeSortSelected("latest");
                        }}
                      >
                        الأكثر مشاهدة
                      </span>
                      <span
                        className={
                          sortSelected === "solved"
                            ? `text-[#333333] border-b-[2px] border-[#999999] pb-1.5 cursor-pointer sm:block hidden`
                            : `text-[#666666] pb-2 cursor-pointer sm:block hidden`
                        }
                        onClick={() => {
                          handleChangeSortSelected("solved");
                        }}
                      >
                        {" "}
                        مُجاب عليه
                      </span>
                      <AnimatePresence>
                        {isTabsDropDownOpen && (
                          <div
                            ref={dropdownRef}
                            initial={{ y: "100vh", opacity: 0 }}
                            animate={{
                              y: 0,
                              opacity: 1,
                              transition: { duration: 0.3 },
                            }}
                            exit={{
                              y: "100vh",
                              opacity: 0,
                              transition: { duration: 0.3 },
                            }}
                            className="sm:!hidden flex flex-col bg-white w-full rounded-t-[10px] p-4 gap-y-4"
                          >
                            <img
                              src="/images/bottom-sheet-anchor.svg"
                              alt="anchor"
                              className="mb-4 self-center"
                            />
                            <span
                              className={
                                sortSelected === "new"
                                  ? `text-[#333333] border-[#999999] cursor-pointer flex gap-3 items-center text-medium`
                                  : ` text-medium text-[#666666] cursor-pointer ms-10`
                              }
                              onClick={() => {
                                handleChangeSortSelected("new");
                                toggleTabsDropDown();
                              }}
                            >
                              <img
                                className={
                                  sortSelected === "new" ? `block` : `hidden`
                                }
                                src="/images/selected.svg"
                                alt=""
                              />{" "}
                              الأحدث
                            </span>
                            <hr className="-mx-4" />
                            <span
                              className={
                                sortSelected === "latest"
                                  ? `text-[#333333] border-[#999999] cursor-pointer flex gap-3 items-center text-medium`
                                  : ` text-medium text-[#666666] cursor-pointer ms-10`
                              }
                              onClick={() => {
                                handleChangeSortSelected("latest");
                                toggleTabsDropDown();
                              }}
                            >
                              <img
                                className={
                                  sortSelected === "latest" ? `block` : `hidden`
                                }
                                src="/images/selected.svg"
                                alt=""
                              />{" "}
                              الأكثر مشاهدة
                            </span>
                            <hr className="-mx-4" />
                            <span
                              className={
                                sortSelected === "solved"
                                  ? `text-[#333333] border-[#999999] cursor-pointer flex gap-3 items-center text-medium`
                                  : ` text-medium text-[#666666] cursor-pointer ms-10`
                              }
                              onClick={() => {
                                handleChangeSortSelected("solved");
                                toggleTabsDropDown();
                              }}
                            >
                              <img
                                className={
                                  sortSelected === "solved" ? `block` : `hidden`
                                }
                                src="/images/selected.svg"
                                alt=""
                              />{" "}
                              مُجاب عليه
                            </span>
                          </div>
                        )}
                      </AnimatePresence>
                    </div>
                    <div className="flex gap-3 items-end">
                      <button
                        type="submit"
                        className="hidden sm:flex items-center justify-center btn px-8 text-[14px] font-medium rounded-[4px] text-[#004D5A] transition-all h-[45px]"
                        onClick={handleCreatePostClick}
                      >
                        <img
                          src="/images/add-fill.svg"
                          alt="+"
                          className="mb-1 me-1"
                        />{" "}
                        أضف منشور جديد
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* post filters and add post shimmers */}
              {(isLoading || postsStatus === "loading") && (
                <div className="max-w-[1000px] lg:mx-auto max-sm:mx-4">
                  <div className="max-sm:block hidden stretch-shimmer mt-3">
                    <div className="shimmer shimmer-button"></div>
                  </div>
                  <div className="sm:pt-10 sm:!flex hidden sm:flex-row flex-col gap-10 items-center text-[18px] max-sm:absolute top-[50px] inset-12 max-sm:p-6 max-sm:bg-[#f8f8f8] max-sm:border rounded-lg h-fit">
                    <ShimmerBadge></ShimmerBadge>
                    <ShimmerBadge width={100}></ShimmerBadge>
                    <ShimmerBadge></ShimmerBadge>
                  </div>
                </div>
              )}

              <div className="mt-2 max-w-[1000px] lg:mx-auto max-sm:mx-4">
                {filteredPosts.length === 0 && !(isLoading || postsLoading) ? (
                  <div className="flex flex-col items-center justify-center mt-10 gap-4">
                    <p dir="ltr" className="text-black text-[80px] text-center">
                      :(
                    </p>
                    <p className="text-black text-[14px] text-center">
                      لا توجد نتائج
                    </p>
                  </div>
                ) : (
                  filteredPosts.map((post, index) => (
                    <React.Fragment key={post.id}>
                      <PostItem
                        post={post}
                        isLoggedIn={isLoggedin}
                        loggedInUserId={user?.id}
                      />
                    </React.Fragment>
                  ))
                )}
                {/* post shimmers only */}
                {postsStatus === "fetching" && (
                  <div className="flex flex-col justify-center gap-y-2">
                    {[...Array(6)].map((_, index) => (
                      <PostShimmer key={index}></PostShimmer>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {!formVisible && (
        <div className="fixed bottom-4 right-4 sm:hidden">
          <button
            onClick={handleCreatePostClick}
            className="bg-[#BAF3E6] border border-secondary text-white rounded-full p-5 shadow-md focus:outline-none"
          >
            <img width={36} src="/images/add-fill.svg" alt="add" />
          </button>
        </div>
      )}
    </>
  );
};

export default Home;
